import { useEffect, useState } from 'react';
import { Controller, type SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { faCalendarCircleMinus } from '@fortawesome/pro-regular-svg-icons/faCalendarCircleMinus';
import { ErrorMessage } from '@hookform/error-message';
import { Button, Col, DatePicker, Form, Row, Space } from 'antd';
import dayjs from 'dayjs';
import { InputValidationError } from 'pos-client/components/generic/input-validation-error/InputValidationError.tsx';
import { PosModal } from 'pos-client/components/shared/Display/Modal/PosModal.tsx';
import { useConfirmModal } from 'pos-client/hooks/useConfirmModal.tsx';
import { useNotifications } from 'pos-client/hooks/useNotifications.tsx';
import { fullWidth } from 'pos-client/utils/cssHelpers.ts';
import { dateFormatString } from 'pos-client/utils/dateHelpers.ts';
import { isDayBeforeToday } from 'pos-client/utils/dayjs.ts';
import { trpc } from 'pos-client/utils/trpc.ts';
import type { NonTradingDatesModel } from 'pos-shared/models/openingHoursGroupSchema.ts';

import { useModalType } from './useModalType.tsx';

interface Props {
  isVisible: boolean;
  data: NonTradingDatesModel | undefined;
  handleOk: () => void;
  handleCancel: () => void;
}

export const CreateEditNonTradingDatesModal = ({
  data,
  isVisible,
  handleOk,
  handleCancel,
}: Props) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { mode, setMode } = useModalType();
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<NonTradingDatesModel>({
    values: {
      id: data?.id || '',
      date: data?.date || '',
    },
  });

  const { showSuccess, showError } = useNotifications();

  const { getAllNonTradingDates } = trpc.useContext();
  const [enableSubmit, setEnableSubmit] = useState(false);

  const modalTitle =
    mode === 'create'
      ? t('locations.trading.createNotTradingDate')
      : t('locations.trading.updateNonTradingDate');

  const { showModal: showUnsavedChangesModal } = useConfirmModal({
    type: 'delete',
    title: t('common.confirmCancel.unsavedChanges'),
    content: t('common.confirmCancel.unsavedChangesConfirm'),
    confirmText: t('common.buttons.discardChanges'),
    confirmProps: {
      ghost: true,
    },
    onConfirm: handleCancel,
  });

  const onCancelModal = () => {
    if (isDirty) {
      showUnsavedChangesModal();
    } else {
      handleCancel();
    }
  };

  const onSuccessMutation = async () => {
    await getAllNonTradingDates.invalidate();
    await handleOk();
  };

  const { mutate: createNonTradingDates, isLoading: isCreating } =
    trpc.createNonTradingDates.useMutation({
      onSuccess: async () => {
        onSuccessMutation();
        showSuccess(
          t('common.notification.success.message', {
            action: 'created',
            entity: 'non-trading date',
          })
        );
      },
      onError: async () => {
        showError(
          t('common.notification.error.message', {
            action: 'create',
            entity: 'non-trading date',
          })
        );
      },
    });

  const { mutate: updateNonTradingDates, isLoading: isUpdating } =
    trpc.updateNonTradingDates.useMutation({
      onSuccess: async () => {
        onSuccessMutation();
        showSuccess(
          t('common.notification.success.message', {
            action: 'updated',
            entity: 'non-trading date',
          })
        );
      },
      onError: async () => {
        showError(
          t('common.notification.error.message', {
            action: 'update',
            entity: 'non-trading date',
          })
        );
      },
    });

  const { mutate: deleteNonTradingDates, isLoading: isDeleting } =
    trpc.deleteNonTradingDates.useMutation({
      onSuccess: async () => {
        onSuccessMutation();
        showSuccess(
          t('common.notification.success.message', {
            action: 'deleted',
            entity: 'non-trading date',
          })
        );
      },
      onError: async () => {
        showError(
          t('common.notification.error.message', {
            action: 'delete',
            entity: 'non-trading date',
          })
        );
      },
    });

  const handleCreateNonTradingDates: SubmitHandler<NonTradingDatesModel> = (
    data
  ) => {
    createNonTradingDates({ openingHoursId: id ?? '', data });
  };

  const handleUpdateNonTradingDates: SubmitHandler<NonTradingDatesModel> = (
    data
  ) => {
    updateNonTradingDates({
      openingHoursId: id || '',
      nonTradingDatesId: data.id || '',
      data: {
        id: data.id,
        date: data.date,
      },
    });
  };

  const resetAll = () => {
    reset({
      date: '',
      id: '',
    });
  };

  const { showModal: confirmRemoveNonTradingDates } = useConfirmModal({
    type: 'delete',
    title: t('locations.trading.removeNonTradingDate'),
    content: t('locations.trading.removeNonTradingDateConfirm'),
    confirmText: t('common.buttons.delete'),
    confirmProps: {
      ghost: true,
    },
  });

  const handleRemoveNonTradingDates: SubmitHandler<
    NonTradingDatesModel
  > = () => {
    confirmRemoveNonTradingDates({
      onConfirm() {
        deleteNonTradingDates({
          openingHoursId: id || '',
          nonTradingDatesId: data?.id || '',
        });
      },
    });
  };

  useEffect(() => {
    setMode(data ? 'edit' : 'create');
  }, [data]);

  useEffect(() => {
    const selectedValue = watch((value) => {
      if (value?.date !== null) {
        setEnableSubmit(true);
      } else {
        setEnableSubmit(false);
      }
    });
    return () => selectedValue.unsubscribe();
  }, [watch]);

  return (
    <PosModal
      header={{ title: modalTitle, icon: faCalendarCircleMinus }}
      open={isVisible}
      onCancel={onCancelModal}
      afterClose={resetAll}
      footer={
        mode === 'edit' ? (
          <Row justify="space-between">
            <Col>
              <Button
                danger
                onClick={handleSubmit(handleRemoveNonTradingDates)}
                loading={isDeleting}
              >
                {t('common.buttons.delete')}
              </Button>
            </Col>
            <Col>
              <Button
                key="submit"
                type="primary"
                onClick={handleSubmit(handleUpdateNonTradingDates)}
                loading={isUpdating}
                disabled={!isDirty || !enableSubmit}
              >
                {t('common.buttons.update')}
              </Button>
            </Col>
          </Row>
        ) : (
          <Row justify="end">
            <Space size="small">
              <Button type="default" onClick={onCancelModal}>
                {t('common.buttons.cancel')}
              </Button>
              <Button
                key="submit"
                type="primary"
                onClick={handleSubmit(handleCreateNonTradingDates)}
                loading={isCreating}
                disabled={!isDirty || !enableSubmit}
              >
                {t('common.buttons.create')}
              </Button>
            </Space>
          </Row>
        )
      }
    >
      <Form layout="vertical">
        <Controller
          control={control}
          name="date"
          render={({ field: { onChange, value, name } }) => (
            <Form.Item label={t('common.date.date')}>
              <DatePicker
                onChange={(value) => {
                  const date = value ? value.format('YYYY-MM-DD') : ''; // we don't need precise time here just date
                  onChange(date);
                }}
                format={dateFormatString}
                value={value ? dayjs(value) : null}
                disabledDate={isDayBeforeToday}
                style={fullWidth}
              />
              <ErrorMessage
                errors={errors}
                name={name}
                render={InputValidationError}
              />
            </Form.Item>
          )}
        />
      </Form>
    </PosModal>
  );
};
